import { useState, useEffect, useRef } from "react";
import { Send, Save, FileText, Trash2, ChevronDown, ChevronUp, Users, Paperclip, X, Globe, Clock, CheckCircle, AlertCircle, Loader2, Search, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import RichTextEditor from "@/components/RichTextEditor";

export function AdminEmailTool() {
  const [recipientData, setRecipientData] = useState({ registrations: [], newsletter: [], waitlist: [], camps: [] });
  const [emailHistory, setEmailHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [view, setView] = useState("compose");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Compose state
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [selectedCampId, setSelectedCampId] = useState("");
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [subjectEn, setSubjectEn] = useState("");
  const [subjectFr, setSubjectFr] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [bodyFr, setBodyFr] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isTranslatingSubject, setIsTranslatingSubject] = useState(false);
  const [isTranslatingBody, setIsTranslatingBody] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [showHistoryDetail, setShowHistoryDetail] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchRecipientData();
    fetchHistory();
    fetchDrafts();
  }, []);

  const fetchRecipientData = async () => {
    try {
      setDataError(null);
      const res = await adminApi.get(`/admin/bulk-email/recipients`);
      setRecipientData(res.data);
      setDataLoaded(true);
    } catch (e) {
      console.error("Failed to load recipients:", e);
      setDataError("Failed to load contacts. Please refresh the page.");
      toast.error("Failed to load email contacts");
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await adminApi.get(`/admin/bulk-email/history`);
      setEmailHistory(res.data);
    } catch (e) {
      console.error("Failed to load history:", e);
    }
  };

  const fetchDrafts = async () => {
    try {
      const res = await adminApi.get(`/admin/bulk-email/drafts`);
      setDrafts(res.data);
    } catch (e) {
      console.error("Failed to load drafts:", e);
    }
  };

  // Build ALL contacts (deduplicated by email)
  const getAllContacts = () => {
    const seen = new Map();
    const add = (email, name, lang, source) => {
      if (!email) return;
      const key = email.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, { email, name: name || email, language: lang || "en", source });
      }
    };
    recipientData.registrations.forEach(r =>
      add(r.parent_email, r.parent_name || r.participant_name, r.language, "registration")
    );
    recipientData.newsletter.forEach(r =>
      add(r.email, r.email, r.language, "newsletter")
    );
    recipientData.waitlist.forEach(r =>
      add(r.parent_email, r.parent_name || r.participant_name, r.language, "waitlist")
    );
    return Array.from(seen.values());
  };

  // Get contacts based on the selected filter
  const getFilteredContacts = () => {
    if (recipientFilter === "all") return getAllContacts();
    if (recipientFilter === "individual") return getAllContacts();

    const seen = new Map();
    const add = (email, name, lang, source) => {
      if (!email) return;
      const key = email.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, { email, name: name || email, language: lang || "en", source });
      }
    };

    if (recipientFilter === "camp" && selectedCampId) {
      recipientData.registrations
        .filter(r => r.camp_id === selectedCampId)
        .forEach(r => add(r.parent_email, r.parent_name || r.participant_name, r.language, "registration"));
    } else if (recipientFilter === "registrations") {
      recipientData.registrations.forEach(r =>
        add(r.parent_email, r.parent_name || r.participant_name, r.language, "registration")
      );
    } else if (recipientFilter === "newsletter") {
      recipientData.newsletter.forEach(r =>
        add(r.email, r.email, r.language, "newsletter")
      );
    } else if (recipientFilter === "waitlist") {
      recipientData.waitlist.forEach(r =>
        add(r.parent_email, r.parent_name || r.participant_name, r.language, "waitlist")
      );
    }
    return Array.from(seen.values());
  };

  const filteredContacts = getFilteredContacts();

  // Apply search filter
  const displayContacts = searchQuery.trim()
    ? filteredContacts.filter(r =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredContacts;

  // Determine who will actually receive the email
  const getFinalRecipients = () => {
    if (recipientFilter === "individual") {
      // Only send to manually selected people
      return filteredContacts.filter(r => selectedEmails.has(r.email.toLowerCase()));
    }
    // For group filters: if user manually deselected some, respect that
    if (selectedEmails.size > 0) {
      return filteredContacts.filter(r => selectedEmails.has(r.email.toLowerCase()));
    }
    // Otherwise send to all in the filtered list
    return filteredContacts;
  };

  const finalRecipients = getFinalRecipients();
  const enCount = finalRecipients.filter(r => r.language !== "fr").length;
  const frCount = finalRecipients.filter(r => r.language === "fr").length;

  const isChecked = (email) => {
    if (recipientFilter === "individual") {
      return selectedEmails.has(email.toLowerCase());
    }
    // For group filters: checked by default unless user started deselecting
    if (selectedEmails.size === 0) return true;
    return selectedEmails.has(email.toLowerCase());
  };

  const toggleRecipient = (email) => {
    const key = email.toLowerCase();
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (recipientFilter !== "individual" && prev.size === 0) {
        // First deselection in group mode: select all except this one
        filteredContacts.forEach(r => next.add(r.email.toLowerCase()));
        next.delete(key);
        return next;
      }
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (recipientFilter === "individual") {
      setSelectedEmails(new Set(filteredContacts.map(r => r.email.toLowerCase())));
    } else {
      setSelectedEmails(new Set()); // empty = all selected in group mode
    }
  };

  const deselectAll = () => {
    if (recipientFilter === "individual") {
      setSelectedEmails(new Set());
    } else {
      // In group mode, deselect all by creating an empty set that's "active"
      setSelectedEmails(new Set(["__none__"]));
    }
  };

  const handleFilterChange = (value) => {
    setRecipientFilter(value);
    setSelectedEmails(new Set());
    setSelectedCampId("");
    setSearchQuery("");
  };

  const handleTranslate = async (field) => {
    const text = field === "subject" ? subjectEn : bodyEn;
    if (!text.trim()) {
      toast.error(`Please write the English ${field} first`);
      return;
    }

    if (field === "subject") setIsTranslatingSubject(true);
    else setIsTranslatingBody(true);

    try {
      const res = await adminApi.post(`/admin/bulk-email/translate`, { text, field_type: field });
      if (field === "subject") setSubjectFr(res.data.translated_text);
      else setBodyFr(res.data.translated_text);
      toast.success(`${field === "subject" ? "Subject" : "Body"} translated to Acadian French`);
    } catch (e) {
      toast.error("Translation failed. Please try again.");
    } finally {
      if (field === "subject") setIsTranslatingSubject(false);
      else setIsTranslatingBody(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await adminApi.post(`/admin/upload`, formData);
        setAttachments(prev => [...prev, { name: file.name, url: res.data.url }]);
      }
      toast.success("File(s) uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!subjectEn.trim()) { toast.error("Please enter an English subject"); return; }
    if (!bodyEn.trim()) { toast.error("Please enter an English body"); return; }
    if (finalRecipients.length === 0) { toast.error("No recipients selected"); return; }

    const hasFrRecipients = finalRecipients.some(r => r.language === "fr");
    if (hasFrRecipients && (!subjectFr.trim() || !bodyFr.trim())) {
      toast.error("Some recipients speak French — please provide French subject and body, or use the translate button");
      return;
    }

    setIsSending(true);
    try {
      const res = await adminApi.post(`/admin/bulk-email/send`, {
        subject_en: subjectEn,
        subject_fr: subjectFr || subjectEn,
        body_en: bodyEn,
        body_fr: bodyFr || bodyEn,
        recipient_emails: finalRecipients,
        attachment_urls: attachments.map(a => a.url)
      });
      toast.success(res.data.message);
      resetForm();
      fetchHistory();
    } catch (e) {
      toast.error("Failed to send emails");
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      const draftData = {
        subject_en: subjectEn,
        subject_fr: subjectFr,
        body_en: bodyEn,
        body_fr: bodyFr,
        recipient_filter: recipientFilter,
        selected_camp_id: selectedCampId,
        selected_recipient_ids: Array.from(selectedEmails),
        attachment_urls: attachments.map(a => a.url)
      };

      if (editingDraftId) {
        await adminApi.put(`/admin/bulk-email/drafts/${editingDraftId}`, { ...draftData, id: editingDraftId });
        toast.success("Draft updated");
      } else {
        await adminApi.post(`/admin/bulk-email/drafts`, draftData);
        toast.success("Draft saved");
      }
      fetchDrafts();
    } catch (e) {
      toast.error("Failed to save draft");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const loadDraft = (draft) => {
    setSubjectEn(draft.subject_en || "");
    setSubjectFr(draft.subject_fr || "");
    setBodyEn(draft.body_en || "");
    setBodyFr(draft.body_fr || "");
    setRecipientFilter(draft.recipient_filter || "all");
    setSelectedCampId(draft.selected_camp_id || "");
    setSelectedEmails(new Set(draft.selected_recipient_ids || []));
    setAttachments((draft.attachment_urls || []).map(url => ({ name: url.split("/").pop(), url })));
    setEditingDraftId(draft.id);
    setView("compose");
    toast.success("Draft loaded");
  };

  const deleteDraft = async (id) => {
    try {
      await adminApi.delete(`/admin/bulk-email/drafts/${id}`);
      toast.success("Draft deleted");
      fetchDrafts();
      if (editingDraftId === id) resetForm();
    } catch (e) {
      toast.error("Failed to delete draft");
    }
  };

  const resetForm = () => {
    setSubjectEn(""); setSubjectFr(""); setBodyEn(""); setBodyFr("");
    setRecipientFilter("all"); setSelectedCampId(""); setSelectedEmails(new Set());
    setAttachments([]); setEditingDraftId(null); setSearchQuery("");
  };

  return (
    <div className="space-y-6" data-testid="email-tool">
      {/* Sub-navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-3">
        <button
          onClick={() => setView("compose")}
          className={`px-4 py-2 text-sm font-heading tracking-wide rounded-sm transition-colors ${view === "compose" ? "bg-burns-red text-white" : "text-zinc-400 hover:text-white"}`}
          data-testid="email-compose-tab"
        >
          <Send className="w-4 h-4 inline mr-2" />COMPOSE
        </button>
        <button
          onClick={() => setView("drafts")}
          className={`px-4 py-2 text-sm font-heading tracking-wide rounded-sm transition-colors ${view === "drafts" ? "bg-burns-red text-white" : "text-zinc-400 hover:text-white"}`}
          data-testid="email-drafts-tab"
        >
          <FileText className="w-4 h-4 inline mr-2" />DRAFTS ({drafts.length})
        </button>
        <button
          onClick={() => setView("history")}
          className={`px-4 py-2 text-sm font-heading tracking-wide rounded-sm transition-colors ${view === "history" ? "bg-burns-red text-white" : "text-zinc-400 hover:text-white"}`}
          data-testid="email-history-tab"
        >
          <Clock className="w-4 h-4 inline mr-2" />HISTORY ({emailHistory.length})
        </button>
      </div>

      {/* Data error banner */}
      {dataError && (
        <div className="bg-red-900/30 border border-red-500/30 px-4 py-3 rounded-sm flex items-center justify-between">
          <span className="text-red-300 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {dataError}
          </span>
          <Button size="sm" variant="ghost" onClick={fetchRecipientData} className="text-red-300 hover:text-white text-xs">
            Retry
          </Button>
        </div>
      )}

      {/* ============== COMPOSE VIEW ============== */}
      {view === "compose" && (
        <div className="space-y-6">
          {editingDraftId && (
            <div className="bg-burns-gold/10 border border-burns-gold/30 px-4 py-2 rounded-sm flex items-center justify-between">
              <span className="text-burns-gold text-sm">Editing draft</span>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white text-xs">Clear & start new</button>
            </div>
          )}

          {/* RECIPIENTS */}
          <div className="bg-zinc-950 border border-white/10 p-4 rounded-sm space-y-4">
            <h3 className="font-heading text-white tracking-wide flex items-center gap-2">
              <Users className="w-5 h-5 text-burns-gold" /> RECIPIENTS
            </h3>

            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-zinc-400 text-xs">Send to</Label>
                <Select value={recipientFilter} onValueChange={handleFilterChange}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="recipient-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone (All Contacts)</SelectItem>
                    <SelectItem value="camp">Camp Registrants</SelectItem>
                    <SelectItem value="registrations">All Registrations</SelectItem>
                    <SelectItem value="newsletter">Newsletter Subscribers</SelectItem>
                    <SelectItem value="waitlist">Waitlist Entries</SelectItem>
                    <SelectItem value="individual">Pick Individuals</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {recipientFilter === "camp" && (
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-zinc-400 text-xs">Select Camp</Label>
                  <Select value={selectedCampId} onValueChange={setSelectedCampId}>
                    <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="camp-filter-select">
                      <SelectValue placeholder="Choose camp..." />
                    </SelectTrigger>
                    <SelectContent>
                      {recipientData.camps.map(camp => (
                        <SelectItem key={camp.id} value={camp.id}>
                          {camp.name} ({recipientData.registrations.filter(r => r.camp_id === camp.id).length} registrants)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Explanation text */}
            <p className="text-zinc-500 text-xs">
              {recipientFilter === "all" && "Sending to all contacts across registrations, newsletter, and waitlist."}
              {recipientFilter === "camp" && !selectedCampId && "Choose a camp above to see its registrants."}
              {recipientFilter === "camp" && selectedCampId && "Sending to all registrants of the selected camp. Uncheck anyone you want to exclude."}
              {recipientFilter === "registrations" && "Sending to all camp registrations. Uncheck anyone you want to exclude."}
              {recipientFilter === "newsletter" && "Sending to all newsletter subscribers. Uncheck anyone you want to exclude."}
              {recipientFilter === "waitlist" && "Sending to all waitlist entries. Uncheck anyone you want to exclude."}
              {recipientFilter === "individual" && "Check the people you want to email below."}
            </p>

            {/* Search */}
            {filteredContacts.length > 3 && (
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent border-white/20 text-white pl-9"
                  placeholder="Search by name or email..."
                  data-testid="recipient-search"
                />
              </div>
            )}

            {/* Recipient list */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-xs">
                  {filteredContacts.length} contacts
                  {recipientFilter === "individual" && ` · ${selectedEmails.size} selected`}
                </span>
                {filteredContacts.length > 0 && (
                  <div className="flex gap-3">
                    <button onClick={selectAll} className="text-burns-gold text-xs hover:underline" data-testid="select-all-btn">Select all</button>
                    <button onClick={deselectAll} className="text-zinc-500 text-xs hover:underline" data-testid="deselect-all-btn">Deselect all</button>
                  </div>
                )}
              </div>

              <div className="max-h-56 overflow-y-auto border border-white/5 rounded-sm">
                {displayContacts.length === 0 ? (
                  <p className="text-zinc-500 text-sm p-4 text-center">
                    {recipientFilter === "camp" && !selectedCampId
                      ? "Select a camp above"
                      : dataLoaded ? "No contacts found" : "Loading contacts..."}
                  </p>
                ) : (
                  displayContacts.map((r, i) => (
                    <label
                      key={r.email}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0"
                      data-testid={`recipient-row-${i}`}
                    >
                      <Checkbox
                        checked={isChecked(r.email)}
                        onCheckedChange={() => toggleRecipient(r.email)}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-white text-sm truncate block">{r.name}</span>
                        <span className="text-zinc-500 text-xs truncate block">{r.email}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${r.language === "fr" ? "bg-blue-900/40 text-blue-300" : "bg-zinc-800 text-zinc-400"}`}>
                        {r.language === "fr" ? "FR" : "EN"}
                      </span>
                      <span className="text-zinc-600 text-xs hidden sm:inline">{r.source}</span>
                    </label>
                  ))
                )}
              </div>

              {/* Summary bar */}
              <div className="flex items-center gap-4 text-xs bg-white/5 px-3 py-2 rounded-sm">
                <span className="text-burns-gold font-medium">{finalRecipients.length} will receive</span>
                <span className="text-zinc-500">{enCount} English</span>
                <span className="text-zinc-500">{frCount} French</span>
              </div>
            </div>
          </div>

          {/* EMAIL CONTENT */}
          <div className="bg-zinc-950 border border-white/10 p-4 rounded-sm space-y-4">
            <h3 className="font-heading text-white tracking-wide flex items-center gap-2">
              <Mail className="w-5 h-5 text-burns-gold" /> EMAIL CONTENT
            </h3>

            {/* Subject EN */}
            <div>
              <Label className="text-zinc-400 text-xs">Subject (English) *</Label>
              <Input
                value={subjectEn}
                onChange={(e) => setSubjectEn(e.target.value)}
                className="bg-transparent border-white/20 text-white"
                placeholder="Enter email subject..."
                data-testid="subject-en-input"
              />
            </div>

            {/* Subject FR */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-zinc-400 text-xs">Subject (Acadian French)</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => handleTranslate("subject")}
                  disabled={isTranslatingSubject || !subjectEn.trim()}
                  className="text-burns-gold hover:text-white text-xs h-7 px-2"
                  data-testid="translate-subject-btn"
                >
                  {isTranslatingSubject ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Globe className="w-3 h-3 mr-1" />}
                  Auto-translate
                </Button>
              </div>
              <Input
                value={subjectFr}
                onChange={(e) => setSubjectFr(e.target.value)}
                className="bg-transparent border-white/20 text-white"
                placeholder="Sujet en français acadien..."
                data-testid="subject-fr-input"
              />
            </div>

            {/* Body EN */}
            <div>
              <Label className="text-zinc-400 text-xs mb-1 block">Body (English) *</Label>
              <RichTextEditor
                value={bodyEn}
                onChange={setBodyEn}
                placeholder="Write your email message…"
                ariaLabel="Email body (English)"
              />
            </div>

            {/* Body FR */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-zinc-400 text-xs">Body (Acadian French)</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => handleTranslate("body")}
                  disabled={isTranslatingBody || !bodyEn.trim()}
                  className="text-burns-gold hover:text-white text-xs h-7 px-2"
                  data-testid="translate-body-btn"
                >
                  {isTranslatingBody ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Globe className="w-3 h-3 mr-1" />}
                  Auto-translate
                </Button>
              </div>
              <RichTextEditor
                value={bodyFr}
                onChange={setBodyFr}
                placeholder="Message en français acadien…"
                ariaLabel="Email body (Acadian French)"
              />
            </div>

            {/* Attachments */}
            <div>
              <Label className="text-zinc-400 text-xs">Attachments</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="border-white/20 text-zinc-300 hover:text-white"
                  data-testid="attach-file-btn"
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Paperclip className="w-4 h-4 mr-1" />}
                  Attach File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-zinc-400 bg-white/5 px-3 py-1.5 rounded-sm">
                      <Paperclip className="w-3 h-3" />
                      <span className="flex-1 truncate">{att.name}</span>
                      <button onClick={() => removeAttachment(i)} className="text-zinc-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleSend}
              disabled={isSending || !subjectEn.trim() || !bodyEn.trim() || finalRecipients.length === 0}
              className="btn-primary rounded-sm flex-1 min-w-[200px]"
              data-testid="send-email-btn"
            >
              {isSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {isSending ? "SENDING..." : `SEND TO ${finalRecipients.length} RECIPIENT${finalRecipients.length !== 1 ? "S" : ""}`}
            </Button>
            <Button
              onClick={handleSaveDraft}
              disabled={isSavingDraft}
              variant="outline"
              className="border-white/20 text-zinc-300 hover:text-white rounded-sm"
              data-testid="save-draft-btn"
            >
              {isSavingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingDraftId ? "UPDATE DRAFT" : "SAVE DRAFT"}
            </Button>
          </div>
        </div>
      )}

      {/* ============== DRAFTS VIEW ============== */}
      {view === "drafts" && (
        <div className="space-y-3">
          {drafts.length === 0 ? (
            <p className="text-zinc-500 text-center py-12">No saved drafts</p>
          ) : (
            drafts.map(draft => (
              <div key={draft.id} className="bg-zinc-950 border border-white/10 p-4 rounded-sm" data-testid={`draft-${draft.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{draft.subject_en || "(no subject)"}</p>
                    {draft.subject_fr && <p className="text-blue-300 text-sm truncate">{draft.subject_fr}</p>}
                    <p className="text-zinc-500 text-xs mt-1">
                      {new Date(draft.updated_at || draft.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {" · "}{draft.recipient_filter || "all"} recipients
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => loadDraft(draft)} className="btn-primary rounded-sm text-xs">
                      LOAD
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteDraft(draft.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ============== HISTORY VIEW ============== */}
      {view === "history" && (
        <div className="space-y-3">
          {emailHistory.length === 0 ? (
            <p className="text-zinc-500 text-center py-12">No emails sent yet</p>
          ) : (
            emailHistory.map(entry => (
              <div key={entry.id} className="bg-zinc-950 border border-white/10 p-4 rounded-sm" data-testid={`history-${entry.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{entry.subject_en}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs">
                      <span className="text-zinc-500">
                        {new Date(entry.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> {entry.sent_count} sent
                      </span>
                      {entry.failed_count > 0 && (
                        <span className="text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {entry.failed_count} failed
                        </span>
                      )}
                      {entry.attachment_count > 0 && (
                        <span className="text-zinc-400 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> {entry.attachment_count} file(s)
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowHistoryDetail(showHistoryDetail === entry.id ? null : entry.id)}
                    className="text-zinc-400 hover:text-white"
                  >
                    {showHistoryDetail === entry.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
                {showHistoryDetail === entry.id && (
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-zinc-500 text-xs mb-1">English Subject</p>
                        <p className="text-white text-sm">{entry.subject_en}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs mb-1">French Subject</p>
                        <p className="text-blue-300 text-sm">{entry.subject_fr}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-zinc-500 text-xs mb-1">English Body</p>
                        <div
                          className="text-zinc-300 text-sm whitespace-pre-wrap bg-white/5 p-3 rounded-sm max-h-40 overflow-y-auto prose prose-invert max-w-none [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-burns-gold"
                          dangerouslySetInnerHTML={{ __html: entry.body_en || "" }}
                        />
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs mb-1">French Body</p>
                        <div
                          className="text-blue-200 text-sm whitespace-pre-wrap bg-white/5 p-3 rounded-sm max-h-40 overflow-y-auto prose prose-invert max-w-none [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-burns-gold"
                          dangerouslySetInnerHTML={{ __html: entry.body_fr || "" }}
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs mb-2">Recipients ({entry.recipients?.length || 0})</p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {(entry.recipients || []).map((r, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            {r.status === "sent"
                              ? <CheckCircle className="w-3 h-3 text-green-400" />
                              : <AlertCircle className="w-3 h-3 text-red-400" />}
                            <span className="text-zinc-300">{r.name || r.email}</span>
                            <span className="text-zinc-600">{r.email}</span>
                            <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${r.language === "fr" ? "bg-blue-900/40 text-blue-300" : "bg-zinc-800 text-zinc-400"}`}>
                              {r.language === "fr" ? "FR" : "EN"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
